import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import api from "../services/api";
import { useSEO, SEO_PAGES } from "../hooks/useSEO";

const C = {
  saffron:"#E8922A", saffronL:"#FDF0D5", saffronD:"#B86A10",
  sage:"#4A7C59", sageL:"#EAF4EE", sageD:"#2D5A3D",
  terra:"#B85C38",
  cream:"#FDFAF3", creamD:"#F5EDD8",
  turmeric:"#F7C948",
  copper:"#8B4513",
  ink:"#2C1810", inkMid:"#5C3D2E", inkLight:"#9A7B60",
};

const Lotus = ({ size=40, color=C.saffron }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    {[0,30,60,-30,-60,90].map((r,i) => (
      <ellipse key={i} cx="40" cy="50" rx="7" ry="16"
        fill={color} opacity={i>3?0.45:i>1?0.7:0.95}
        transform={`rotate(${r} 40 50)`}/>
    ))}
    <circle cx="40" cy="44" r="7" fill={C.turmeric}/>
    <rect x="38" y="58" width="4" height="10" rx="2" fill={C.sage} opacity="0.55"/>
  </svg>
);

const Leaf = ({ size=44, color=C.sage, rot=0 }) => (
  <svg width={size} height={size} viewBox="0 0 60 60" fill="none"
    style={{ transform:`rotate(${rot}deg)` }}>
    <path d="M30 6 C52 6 58 30 30 54 C2 30 8 6 30 6Z" fill={color} opacity="0.85"/>
    <path d="M30 6 L30 54" stroke="white" strokeWidth="1.4" opacity="0.5"/>
  </svg>
);

/* Service icons cycle */
const SERVICE_ICONS = ["🌿","🧘","🌸","🍵","✨","🌺","🌾","💧","🌙","☀️"];
const SERVICE_COLORS = [
  { bg:C.saffronL, border:C.saffron, accent:C.saffronD },
  { bg:C.sageL,    border:C.sage,    accent:C.sageD    },
  { bg:"#FFF5F0",  border:C.terra,   accent:C.terra    },
  { bg:"#F0FFF4",  border:C.sage,    accent:C.sageD    },
  { bg:"#F0F4FF",  border:"#9BB5E8", accent:"#3B5CB8"  },
  { bg:C.saffronL, border:C.saffron, accent:C.saffronD },
];

const DEFAULT_SERVICES = [
  { title:"Ayurvedic Consultation", desc:"Classical nadi pariksha and dosha assessment for a fully personalized healing plan." },
  { title:"Pañcakarma Therapy",     desc:"Complete five-fold detoxification and deep-tissue rejuvenation of body and mind." },
  { title:"Diet & Lifestyle",       desc:"Prakṛti-based nutrition and seasonal routine for sustained, long-term wellbeing." },
  { title:"Shirodhara",             desc:"Warm medicated oil stream on the forehead for profound mental calm and clarity." },
  { title:"Yoga & Pranayama",       desc:"Breath-led movement practices tailored precisely to your unique constitution." },
  { title:"Herbal Medicines",       desc:"Authentic classical formulations prepared fresh — zero synthetic additives." },
];

export default function Services() {
  useSEO(SEO_PAGES.services);
  const [content, setContent] = useState({});

  useEffect(() => {
    api.get("/content/services").then(res => {
      setContent(res.data?.data || {});
    }).catch(()=>{});
  }, []);

  const dynamicServices = [
    content.service1, content.service2, content.service3,
    content.service4, content.service5, content.service6,
  ].filter(Boolean);

  const services = dynamicServices.length
    ? dynamicServices.map((title, i) => ({ title, desc: content[`service${i+1}Desc`] || "Personalized, classical treatment aligned with your prakṛti and current state of health." }))
    : DEFAULT_SERVICES;

  return (
    <div style={{ background:C.cream, minHeight:"100vh", paddingTop:72 }}>

      {/* HERO */}
      <section style={{
        position:"relative", padding:"72px 24px 60px",
        background:`linear-gradient(145deg,${C.cream} 0%,${C.creamD} 45%,${C.saffronL} 100%)`,
        overflow:"hidden", textAlign:"center"
      }}>
        <div style={{ position:"absolute", left:"3%", top:"8%", opacity:0.12, pointerEvents:"none" }}><Leaf size={56} color={C.sage} rot={20}/></div>
        <div style={{ position:"absolute", right:"3%", top:"10%", opacity:0.06, pointerEvents:"none" }}><Lotus size={180} color={C.saffron}/></div>
        <div style={{ position:"absolute", right:"5%", bottom:"8%", opacity:0.10, pointerEvents:"none" }}><Leaf size={48} color={C.sage} rot={-30}/></div>

        <motion.div initial={{ opacity:0, y:28 }} animate={{ opacity:1, y:0 }}
          transition={{ duration:0.6 }} style={{ position:"relative", zIndex:2, maxWidth:680, margin:"0 auto" }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginBottom:12,
            background:C.saffronL, border:`1px solid ${C.saffron}40`,
            borderRadius:20, padding:"4px 16px" }}>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.13em",
              color:C.saffronD, textTransform:"uppercase",
              fontFamily:"'Inter',sans-serif" }}>{content.svcTag || "Our Specialities"}</span>
          </div>
          <h1 style={{ fontFamily:"'Playfair Display',serif",
            fontSize:"clamp(30px,5vw,56px)", fontWeight:800,
            color:C.ink, lineHeight:1.18, marginBottom:18 }}>
            {content.title || "Treatments We Offer"}
          </h1>
          <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }}
            transition={{ delay:0.35, duration:0.5 }}
            style={{ display:"flex", alignItems:"center", justifyContent:"center",
              gap:14, marginBottom:20 }}>
            <div style={{ height:1, width:60, background:`linear-gradient(90deg,transparent,${C.saffron})` }}/>
            <Lotus size={34} color={C.saffron}/>
            <div style={{ height:1, width:60, background:`linear-gradient(90deg,${C.saffron},transparent)` }}/>
          </motion.div>
          <p style={{ fontSize:"clamp(14px,2vw,17px)", color:C.inkMid, lineHeight:1.85,
            fontFamily:"'Merriweather',serif" }}>
            {content.intro || "Time-tested Ayurvedic therapies, each adapted to your constitution and current state of health."}
          </p>
        </motion.div>
      </section>

      {/* SERVICES GRID */}
      <section style={{ padding:"72px 24px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <motion.div
            variants={{ hidden:{ opacity:0 }, show:{ opacity:1, transition:{ staggerChildren:0.10 } } }}
            initial="hidden" animate="show"
            style={{ display:"grid",
              gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:24 }}>
            {services.map((service, i) => {
              const col = SERVICE_COLORS[i % SERVICE_COLORS.length];
              const icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
              return (
                <motion.div key={i}
                  variants={{ hidden:{ opacity:0, y:20 }, show:{ opacity:1, y:0, transition:{ duration:0.4, ease:"easeOut" } } }}
                  whileHover={{ y:-8, boxShadow:"0 24px 50px rgba(0,0,0,0.11)" }}
                  style={{ background:"#fff", borderRadius:22, padding:"32px 28px",
                    boxShadow:"0 4px 22px rgba(0,0,0,0.065)",
                    border:`1px solid ${col.border}25`,
                    position:"relative", overflow:"hidden" }}>
                  {/* Background glow */}
                  <div style={{ position:"absolute", top:-30, right:-30,
                    width:120, height:120, borderRadius:"50%",
                    background:col.bg, opacity:0.6, pointerEvents:"none" }}/>

                  <div style={{ position:"relative" }}>
                    {/* Icon badge */}
                    <div style={{ width:52, height:52, borderRadius:14,
                      background:col.bg, border:`1.5px solid ${col.border}30`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:26, marginBottom:18 }}>{icon}</div>

                    <h3 style={{ fontFamily:"'Playfair Display',serif",
                      fontSize:19, fontWeight:700, color:C.ink, marginBottom:10 }}>
                      {typeof service === "string" ? service : service.title}
                    </h3>
                    <p style={{ fontSize:13.5, color:C.inkMid, lineHeight:1.7,
                      fontFamily:"'Merriweather',serif" }}>
                      {service.desc || "Personalized, classical treatment aligned with your prakṛti and current state of health."}
                    </p>

                    {/* Bottom accent */}
                    <div style={{ height:3, width:40, borderRadius:2, marginTop:18,
                      background:`linear-gradient(90deg,${col.border},transparent)` }}/>

                    {/* Per-service Book button */}
                    <motion.a
                      href="/request"
                      whileHover={{ scale:1.04, boxShadow:`0 10px 28px rgba(232,146,42,0.40)` }}
                      whileTap={{ scale:0.97 }}
                      style={{ display:"inline-flex", alignItems:"center", gap:6,
                        marginTop:20, padding:"9px 22px", borderRadius:50,
                        fontSize:13, fontWeight:700, textDecoration:"none",
                        fontFamily:"'Inter',sans-serif",
                        background:`linear-gradient(135deg,${col.border},${col.accent})`,
                        color:"#fff",
                        boxShadow:`0 4px 16px rgba(0,0,0,0.12)` }}>
                      🌿 Book This Treatment
                    </motion.a>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* INFO BAND */}
      <section style={{
        background:`linear-gradient(135deg,${C.sageD},${C.sage})`,
        padding:"56px 24px", position:"relative", overflow:"hidden"
      }}>
        <div style={{ position:"absolute", right:-40, top:-40, opacity:0.06 }}>
          <Lotus size={260} color="#fff"/>
        </div>
        <div style={{ maxWidth:860, margin:"0 auto",
          display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",
          gap:32, textAlign:"center", position:"relative" }}>
          {[
            { icon:"🌿", n:content.statBand1N||"100%",  label:content.statBand1L||"Natural Ingredients" },
            { icon:"📜", n:content.statBand2N||"5000+", label:content.statBand2L||"Years of Wisdom"      },
            { icon:"🏥", n:content.statBand3N||"25+",   label:content.statBand3L||"Therapies Available"  },
            { icon:"💚", n:content.statBand4N||"Safe",  label:content.statBand4L||"Evidence-Based Care"  },
          ].map((s,i) => (
            <motion.div key={i} initial={{ opacity:0, y:20 }}
              whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
              transition={{ delay:i*0.1 }}>
              <div style={{ fontSize:32, marginBottom:8 }}>{s.icon}</div>
              <div style={{ fontFamily:"'Playfair Display',serif",
                fontSize:"clamp(28px,4vw,44px)", fontWeight:800,
                color:C.turmeric, lineHeight:1 }}>{s.n}</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.72)",
                marginTop:6, fontFamily:"'Merriweather',serif" }}>{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding:"68px 24px", background:"#fff", textAlign:"center" }}>
        <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
          viewport={{ once:true }}>
          <motion.div animate={{ y:[-4,4,-4] }} transition={{ duration:3, repeat:Infinity }}>
            <Lotus size={52} color={C.saffron}/>
          </motion.div>
          <h2 style={{ fontFamily:"'Playfair Display',serif",
            fontSize:"clamp(22px,3.5vw,36px)", fontWeight:700,
            color:C.ink, margin:"16px 0 10px" }}>
            {content.svcCtaHeading || "Begin Your Healing Journey Today"}
          </h2>
          <p style={{ fontSize:16, color:C.inkLight, marginBottom:28,
            fontFamily:"'Merriweather',serif" }}>
            {content.svcCtaSub || "Schedule a consultation and take the first step towards holistic wellness."}
          </p>
          <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
            <motion.a href="/request"
              whileHover={{ scale:1.04, boxShadow:"0 14px 34px rgba(232,146,42,0.50)" }}
              whileTap={{ scale:0.97 }}
              style={{ display:"inline-flex", alignItems:"center", gap:8,
                padding:"13px 32px", borderRadius:50, fontSize:15, fontWeight:600,
                textDecoration:"none", fontFamily:"'Inter',sans-serif",
                background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
                color:"#fff", boxShadow:`0 8px 26px rgba(232,146,42,0.38)` }}>
              🌿 {content.svcCtaBtn || "Book Appointment"}
            </motion.a>
            <motion.a href="/about"
              whileHover={{ scale:1.04 }} whileTap={{ scale:0.97 }}
              style={{ display:"inline-flex", alignItems:"center", gap:8,
                padding:"13px 32px", borderRadius:50, fontSize:15, fontWeight:600,
                textDecoration:"none", fontFamily:"'Inter',sans-serif",
                border:`2px solid ${C.saffron}`, color:C.saffron,
                background:"transparent" }}>
              {content.svcCtaBtn2 || "Learn About Us"}
            </motion.a>
          </div>
        </motion.div>
      </section>

    </div>
  );
}
